import { bootstrap, optIn, optOut } from 'vue-gtag'

const SITE_GA4_ID = 'G-1QL8XKQKRF'
const LAVANDULA_GA4_ID = 'G-PDL0JS4B8H'
const GA4_SCRIPT_ID = 'whales-ga4-script'
const GA4_LOAD_TIMEOUT_MS = 12000

// The previous combined choice cannot authorize separate Analytics and Advertising categories.
export const COOKIE_CONSENT_KEY = 'whales.lavandula-cookie-consent.v3'
export const OPEN_COOKIE_SETTINGS_EVENT = 'whales:open-cookie-settings'
export const ANALYTICS_LOAD_ERROR_EVENT = 'whales:analytics-load-error'

export type CookiePreferences = { analytics: boolean; advertising: boolean }

type AnalyticsWindow = Window & {
  dataLayer?: IArguments[]
  gtag?: (...args: unknown[]) => void
}

let trackerInitialized = false
let scriptLoadPromise: Promise<void> | null = null
let defaultConsentQueued = false
let landingConfigured = false
let landingRouteActive = false
let landingConsentActive = false
let landingPageViewTracked = false

const deniedConsent = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
}

function getGtag() {
  const analyticsWindow = window as AnalyticsWindow
  analyticsWindow.dataLayer = analyticsWindow.dataLayer || []
  if (!analyticsWindow.gtag) {
    analyticsWindow.gtag = function gtag() {
      analyticsWindow.dataLayer?.push(arguments)
    }
  }
  return analyticsWindow.gtag
}

function queueDefaultDeniedConsent() {
  if (defaultConsentQueued || trackerInitialized) return
  getGtag()('consent', 'default', deniedConsent)
  defaultConsentQueued = true
}

function queueAnalyticsConsent(granted: boolean) {
  getGtag()('consent', 'update', {
    ...deniedConsent,
    analytics_storage: granted ? 'granted' : 'denied',
  })
}

function initializeTracker() {
  if (trackerInitialized) return
  bootstrap()
  trackerInitialized = true
}

function loadGoogleTag(measurementId: string) {
  const loadedScript = document.querySelector<HTMLScriptElement>(`#${GA4_SCRIPT_ID}[data-loaded="true"]`)
  if (loadedScript) return Promise.resolve()
  if (scriptLoadPromise) return scriptLoadPromise

  scriptLoadPromise = new Promise<void>((resolve, reject) => {
    document.getElementById(GA4_SCRIPT_ID)?.remove()

    const script = document.createElement('script')
    const timeout = window.setTimeout(() => {
      script.onload = null
      script.onerror = null
      script.remove()
      reject(new Error('Google Analytics load timed out.'))
    }, GA4_LOAD_TIMEOUT_MS)

    script.id = GA4_SCRIPT_ID
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
    script.onload = () => {
      window.clearTimeout(timeout)
      script.dataset.loaded = 'true'
      resolve()
    }
    script.onerror = () => {
      window.clearTimeout(timeout)
      script.remove()
      reject(new Error('Google Analytics failed to load.'))
    }
    document.head.appendChild(script)
  }).catch(error => {
    scriptLoadPromise = null
    throw error
  })

  return scriptLoadPromise
}

export function getCookiePreferences(): CookiePreferences | null {
  try {
    const value = window.localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!value) return null
    const preferences = JSON.parse(value)
    return typeof preferences.analytics === 'boolean' && typeof preferences.advertising === 'boolean'
      ? { analytics: preferences.analytics, advertising: preferences.advertising }
      : null
  } catch {
    return null
  }
}

export function saveCookiePreferences(preferences: CookiePreferences) {
  try {
    window.localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(preferences))
  } catch {
    // Consent still applies for the current page when storage is unavailable.
  }
}

function deleteAnalyticsCookies() {
  const cookieNames = document.cookie
    .split(';')
    .map(cookie => cookie.split('=')[0].trim())
    .filter(name => name === '_ga' || name.startsWith('_ga_') || name === '_gid' || name.startsWith('_gat'))

  const hostname = window.location.hostname
  const domainParts = hostname.split('.')
  const domains = ['', hostname]

  if (domainParts.length > 1 && hostname !== 'localhost') {
    domains.push(`.${domainParts.slice(-2).join('.')}`)
  }

  for (const name of new Set(cookieNames)) {
    for (const domain of domains) {
      const domainAttribute = domain ? `; domain=${domain}` : ''
      document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax${domainAttribute}`
    }
  }
}

export function setLandingAnalyticsConsent(granted: boolean) {
  if (!granted) {
    suspendAnalytics()
    return Promise.resolve()
  }
  queueDefaultDeniedConsent()
  queueAnalyticsConsent(true)
  landingConsentActive = true
  const loading = startLandingAnalytics()
  trackLandingPageView()
  return loading
}

export function startAnalytics() {
  setLandingTagDisabled(true)
  optIn()
  initializeTracker()
  return loadGoogleTag(SITE_GA4_ID)
}

function setLandingTagDisabled(disabled: boolean) {
  const analyticsWindow = window as unknown as Record<string, unknown>
  analyticsWindow[`ga-disable-${LAVANDULA_GA4_ID}`] = disabled
}

function startLandingAnalytics() {
  initializeTracker()
  optOut()
  setLandingTagDisabled(false)
  if (!landingConfigured) {
    getGtag()('config', LAVANDULA_GA4_ID, { send_page_view: false, groups: 'lavandula' })
    landingConfigured = true
  }
  return loadGoogleTag(LAVANDULA_GA4_ID)
}

export function trackLandingPageView() {
  if (!landingRouteActive || !landingConsentActive || landingPageViewTracked) return
  getGtag()('event', 'page_view', {
    send_to: LAVANDULA_GA4_ID,
    page_location: window.location.href,
    page_path: '/lavandula/vip',
  })
  landingPageViewTracked = true
}

export function suspendAnalytics() {
  landingConsentActive = false
  landingPageViewTracked = false
  setLandingTagDisabled(true)
  if (trackerInitialized) queueAnalyticsConsent(false)
  optOut()
  deleteAnalyticsCookies()
}

export function applyAnalyticsForRoute(isLavandulaLandingPage: boolean) {
  if (isLavandulaLandingPage && !landingRouteActive) landingPageViewTracked = false
  landingRouteActive = isLavandulaLandingPage
  if (!isLavandulaLandingPage) {
    landingConsentActive = false
    landingPageViewTracked = false
    if (trackerInitialized) queueAnalyticsConsent(true)
    void startAnalytics().catch(reportAnalyticsLoadError)
    return
  }

  queueDefaultDeniedConsent()
  if (getCookiePreferences()?.analytics) {
    landingConsentActive = true
    queueAnalyticsConsent(true)
    void startLandingAnalytics().catch(reportAnalyticsLoadError)
  } else {
    suspendAnalytics()
  }
}

export function reportAnalyticsLoadError(error: unknown) {
  console.error('Unable to load Google Analytics after consent.', error)
  window.dispatchEvent(new CustomEvent(ANALYTICS_LOAD_ERROR_EVENT))
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT))
}
